import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Box, Button, Grid, TextField, Typography } from "@material-ui/core";
import { CheckCircle, Cancel, Edit, RateReview, Refresh } from "@material-ui/icons";

import SectionCard from "../../utils/SectionCard";
import {
  reviewByAdmin,
  suggestResearchCode,
} from "../../../../reducer/internalResearch/action";
import {
  INT_REVIEW_ACTION_ACCEPT,
  INT_REVIEW_ACTION_REJECT,
  INT_REVIEW_ACTION_ADJUST_REQUEST,
} from "../../../../constants/internalResearchStatus";
import { getEndYear } from "../../../../utils/researchCodeYear";

// ReviewByAdminForm — vòng duyệt Phòng QLKH cho đề tài Cơ sở.
// Khác ReviewForm chuẩn: thêm ô **Mã đề tài** cho admin gán/sửa khi accept.
//   - Lần đầu mở (đề tài chưa có code) → pre-fill bằng BE suggest `ĐTCS_<khoa>_{yy}.{seq}`.
//   - Đề tài đã có code (resubmit / re-review) → pre-fill bằng code hiện tại.
//   - Admin có thể sửa tự do trước khi bấm Duyệt.
export default function ReviewByAdminForm({ detail, onDone }) {
  const dispatch = useDispatch();
  const [comment, setComment] = useState("");
  const [researchCode, setResearchCode] = useState(detail?.researchCode || "");
  const [pending, setPending] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);

  // endYear — năm của ngày kết thúc đề tài, quyết định 2 số {yy} của mã
  // (ĐTCS có loại 1 năm và 2 năm nên không suy ra được từ năm học).
  const endYear = getEndYear(detail?.endTime);

  // Pre-fill code khi mở form: nếu đề tài chưa có code, gọi BE suggest.
  useEffect(() => {
    const fillSuggest = async () => {
      if (detail?.researchCode) return; // đã có, không cần suggest
      if (!detail?.schoolYear) return;
      setSuggestLoading(true);
      try {
        const s = await suggestResearchCode(
          detail.schoolYear,
          detail?.departmentId,
          endYear
        );
        if (s) setResearchCode(s);
      } finally {
        setSuggestLoading(false);
      }
    };
    fillSuggest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id]);

  // Refresh suggest — admin bấm để load lại nếu có đề tài khác vừa được duyệt.
  const handleRefreshSuggest = async () => {
    if (!detail?.schoolYear) return;
    setSuggestLoading(true);
    try {
      const s = await suggestResearchCode(
        detail.schoolYear,
        detail?.departmentId,
        endYear
      );
      if (s) setResearchCode(s);
    } finally {
      setSuggestLoading(false);
    }
  };

  const handle = async (action) => {
    if (
      (action === INT_REVIEW_ACTION_ADJUST_REQUEST || action === INT_REVIEW_ACTION_REJECT) &&
      !comment.trim()
    ) {
      alert("Vui lòng nhập nhận xét / lý do.");
      return;
    }
    if (action === INT_REVIEW_ACTION_ACCEPT && !researchCode.trim()) {
      alert("Vui lòng nhập Mã đề tài (hoặc giữ mã gợi ý) trước khi duyệt.");
      return;
    }
    setPending(action);
    try {
      const ok = await dispatch(reviewByAdmin(detail.id, action, comment, researchCode.trim()));
      if (ok && onDone) onDone();
    } finally {
      setPending(null);
    }
  };

  return (
    <Box padding="16px 24px">
      <SectionCard icon={<RateReview />} title="Kết luận vòng Phòng QLKH" marginTop={0}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>Mã đề tài</b>{" "}
              <span style={{ color: "#c62828" }}>*</span>
              <Typography component="span" variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                (gợi ý tự sinh, admin sửa tuỳ ý)
              </Typography>
            </Typography>
            <Box display="flex" gridGap={8} alignItems="center">
              <TextField
                fullWidth size="small" variant="outlined"
                value={researchCode}
                disabled={suggestLoading || pending !== null}
                onChange={(e) => setResearchCode(e.target.value)}
                placeholder="VD: ĐTCS_CNTT_26.01"
              />
              <Button
                size="small" variant="outlined"
                startIcon={<Refresh />} disabled={suggestLoading || pending !== null}
                onClick={handleRefreshSuggest}
              >
                Gợi ý
              </Button>
            </Box>
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
              Mã này chỉ áp dụng khi <b>Duyệt</b>. Nếu Yêu cầu chỉnh sửa / Loại, mã hiện tại sẽ giữ nguyên.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth multiline minRows={4} variant="outlined"
              label="Nhận xét / lý do"
              placeholder="Bắt buộc khi Yêu cầu chỉnh sửa hoặc Loại"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={pending !== null}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <Box marginTop={3} paddingTop={2} display="flex" gridGap={12} justifyContent="flex-end" borderTop="solid 1px #e0e0e0">
        <Button
          variant="contained"
          style={{ backgroundColor: "#b52f2f", color: "#fff" }}
          startIcon={<Cancel />} disabled={pending !== null}
          onClick={() => handle(INT_REVIEW_ACTION_REJECT)}
        >
          Loại
        </Button>
        <Button
          variant="outlined" color="secondary"
          startIcon={<Edit />} disabled={pending !== null}
          onClick={() => handle(INT_REVIEW_ACTION_ADJUST_REQUEST)}
        >
          Yêu cầu chỉnh sửa
        </Button>
        <Button
          variant="contained" color="primary"
          startIcon={<CheckCircle />} disabled={pending !== null}
          onClick={() => handle(INT_REVIEW_ACTION_ACCEPT)}
        >
          Duyệt
        </Button>
      </Box>
    </Box>
  );
}
